import { Injectable, effect, inject, signal, untracked } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { CurrentContextService } from '../context/current-context.service';
import { NavigationTreeService } from '../navigation/navigation-tree.service';
import { isWorkScreen, normalizeRoute, routeContains } from './wayfinding-navigator';
import { WayfindingRecent } from './wayfinding.models';

@Injectable({ providedIn: 'root' })
export class WayfindingRecentsService {
  private static readonly maxRecents = 8;
  private readonly router = inject(Router);
  private readonly context = inject(CurrentContextService);
  private readonly navigationTree = inject(NavigationTreeService);
  private readonly recentsState = signal<WayfindingRecent[]>([]);

  readonly recents = this.recentsState.asReadonly();

  constructor() {
    effect(() => {
      const key = this.storageKey();
      untracked(() => this.hydrate(key));
    });
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(event => this.record(event.urlAfterRedirects));
    this.record(this.router.url);
  }

  record(url: string): void {
    const route = normalizeRoute(url);
    if (!isWorkScreen(route)) {
      return;
    }

    const next: WayfindingRecent = { route, ...this.labelFor(route) };
    this.recentsState.update(current => {
      const filtered = current.filter(item => normalizeRoute(item.route) !== route);
      return [next, ...filtered].slice(0, WayfindingRecentsService.maxRecents);
    });
    this.persist();
  }

  private labelFor(route: string): Pick<WayfindingRecent, 'labelKey' | 'fallbackLabel'> {
    const match = this.navigationTree.visibleFlatItems()
      .filter(item => routeContains(route, item.route))
      .sort((a, b) => normalizeRoute(b.route).length - normalizeRoute(a.route).length)[0];

    if (match) {
      return { labelKey: match.label, fallbackLabel: match.route };
    }

    const tail = route.split('/').filter(Boolean).slice(-2).join(' / ');
    return { fallbackLabel: tail || route };
  }

  private storageKey(): string {
    return `ain.admin.wayfinding.recents.${this.context.context()?.tenantId ?? 'none'}`;
  }

  private hydrate(key: string): void {
    try {
      const raw = globalThis.localStorage?.getItem(key);
      const parsed = raw ? JSON.parse(raw) as WayfindingRecent[] : [];
      this.recentsState.set(
        Array.isArray(parsed)
          ? parsed.filter(item => item?.route && isWorkScreen(item.route)).slice(0, WayfindingRecentsService.maxRecents)
          : []
      );
    } catch {
      this.recentsState.set([]);
    }
  }

  private persist(): void {
    try {
      globalThis.localStorage?.setItem(this.storageKey(), JSON.stringify(this.recentsState()));
    } catch {
      // Ignore localStorage write failures.
    }
  }
}
